import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Modal } from '../common/Modal';
import { FileUploadZone } from '../pilgrimGuide/upload/FileUploadZone';
import { ValidationErrors } from '../pilgrimGuide/upload/ValidationErrors';
import { WarningMessage } from '../pilgrimGuide/upload/WarningMessage';
import { Download, Info } from 'lucide-react';
import * as XLSX from 'xlsx';
import toast from 'react-hot-toast';
import { useLanguage } from '../../contexts/LanguageContext';
import { useUpgradeStore } from '../../stores/upgradeStore';

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

export const BulkAnswerOrganizerModal = ({ isOpen, onClose }: Props) => {
  const { translations } = useLanguage();
  const [file, setFile] = useState<File | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const navigate = useNavigate();
  const { requests } = useUpgradeStore();
  const userId = localStorage.getItem('userId');

  // Get pending approval requests for this organizer - now including both approved and payment_pending statuses
  const pendingRequests = Object.values(requests).filter(
    req => (req.status === 'approved' || req.status === 'payment_pending') && req.organizerId === userId
  );

  const handleDownloadTemplate = () => {
    // Create template data from pending requests
    const template = pendingRequests.map(request => ({
      pilgrim_name: request.pilgrimName,
      passport_number: request.passportNumber,
      hotel_name: request.fromHotel,
      price: request.price || 0,
      status: '' // To be filled by organizer: "accept" or "reject"
    }));

    const ws = XLSX.utils.json_to_sheet(template);
    
    // Add headers with instructions
    XLSX.utils.sheet_add_aoa(ws, [[
      'Pilgrim Name',
      'Passport Number',
      'Hotel Name',
      'Price (SAR)',
      'Status (enter "accept" or "reject")'
    ]], { origin: 'A1' });

    // Set column widths
    ws['!cols'] = [
      { wch: 30 }, // Pilgrim Name
      { wch: 20 }, // Passport Number
      { wch: 25 }, // Hotel Name
      { wch: 15 }, // Price
      { wch: 25 }, // Status
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Template');
    XLSX.writeFile(wb, 'room-upgrade-approvals-template.xlsx');
  };

  const handleUpload = () => {
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const data = e.target?.result;
      if (data) {
        // Close modal first
        onClose();
        
        // Navigate to status page with file data
        navigate('/bulk-organizer-answer-status', {
          state: { fileData: data }
        });
      }
    };
    reader.readAsBinaryString(file);
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose}
      title={translations?.roomUpgrade?.bulkOrganizerAnswer?.title || 'Bulk Payment Approval'}
    >
      <div className="space-y-6">
        {/* Introduction */}
        <div className="bg-red-50 p-4 rounded-lg">
          <div className="flex items-start gap-3">
            <Info className="text-red-600 shrink-0 mt-0.5" size={20} />
            <div>
              <h4 className="font-medium text-red-800 mb-2">Balance Deduction Information</h4>
              <p className="text-sm text-red-700">
                Approving these upgrades will result in a deduction from your organization's balance. 
                The template shows the amount that will be deducted for each upgrade. You can accept 
                or reject each upgrade individually.
              </p>
              <p className="text-sm text-red-700 mt-2">
                <strong>Important:</strong> Enter "accept" to approve or "reject" to decline each request in the Status column.
              </p>
            </div>
          </div>
        </div>

        {pendingRequests.length === 0 ? (
          <div className="bg-gray-50 p-4 rounded-lg text-center text-gray-500">
            You don't have any pending upgrade requests that need payment approval.
          </div>
        ) : (
          <>
            {/* Template Download Button */}
            <button
              onClick={handleDownloadTemplate}
              className="flex items-center gap-2 text-primary-600 hover:text-primary-700"
            >
              <Download size={20} />
              <span>
                {translations?.roomUpgrade?.bulkOrganizerAnswer?.downloadTemplate || 
                  `Download Excel Template (${pendingRequests.length} pending requests)`}
              </span>
            </button>

            {/* File Upload */}
            <FileUploadZone
              file={file}
              onFileSelect={setFile}
              onFileRemove={() => {
                setFile(null);
                setValidationErrors([]);
              }}
            />

            <ValidationErrors errors={validationErrors} />
            
            {/* Instructions */}
            <div className="bg-blue-50 p-4 rounded-lg text-blue-700">
              <h4 className="font-medium mb-2">
                {translations?.roomUpgrade?.bulkOrganizerAnswer?.instructions?.title || 'Instructions'}
              </h4>
              <ul className="list-disc list-inside space-y-1 text-sm">
                <li>
                  {translations?.roomUpgrade?.bulkOrganizerAnswer?.instructions?.statusField || 
                    'In the "Status" column, type "accept" or "reject" for each request'}
                </li>
                <li>
                  {translations?.roomUpgrade?.bulkOrganizerAnswer?.instructions?.passport || 
                    'Do not modify the passport numbers - they are used to match requests'}
                </li>
                <li>
                  {translations?.roomUpgrade?.bulkOrganizerAnswer?.instructions?.price ||
                    'The price shown is the amount that will be deducted from your balance for each upgrade'}
                </li>
                <li>
                  {translations?.roomUpgrade?.bulkOrganizerAnswer?.instructions?.review ||
                    'Review the full list carefully before uploading'}
                </li>
              </ul>
            </div>
          </>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            {translations?.roomUpgrade?.bulkOrganizerAnswer?.cancel || 'Cancel'}
          </button>
          <button
            onClick={handleUpload}
            disabled={!file || pendingRequests.length === 0}
            className={`px-6 py-2 rounded-lg text-white
              ${!file || pendingRequests.length === 0
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-primary-500 hover:bg-primary-600'}`}
          >
            {translations?.roomUpgrade?.bulkOrganizerAnswer?.upload || 'Upload Responses'}
          </button>
        </div>
      </div>
    </Modal>
  );
};
